import { jsx } from 'react/jsx-runtime';
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { initializeDevCycle } from '@devcycle/js-client-sdk';
import hoistNonReactStatics from 'hoist-non-react-statics';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise */

var extendStatics = function(d, b) {
    extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
    return extendStatics(d, b);
};

function __extends(d, b) {
    if (typeof b !== "function" && b !== null)
        throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

var context = /*#__PURE__*/createContext(undefined);
var Provider = context.Provider;
    context.Consumer;
var initializedContext = /*#__PURE__*/createContext({
  isInitialized: false
});

var initializeDevCycleClient = function initializeDevCycleClient(sdkKey, user, options) {
  if (user === void 0) {
    user = {
      isAnonymous: true
    };
  }

  if (options === null || options === void 0 ? void 0 : options.deferInitialization) {
    return initializeDevCycle(sdkKey, __assign(__assign({}, options), {
      deferInitialization: true
    }));
  }

  return initializeDevCycle(sdkKey, user, options);
};

/**
 * @deprecated Use the `useIsDevCycleInitialized` hook to block rendering of your application
 *             until SDK initialization is complete
 */

function asyncWithDVCProvider(config) {
  return __awaiter(this, void 0, void 0, function () {
    var user, options, sdkKey, client;
    return __generator(this, function (_a) {
      switch (_a.label) {
        case 0:
          user = config.user, options = config.options;

          if ('sdkKey' in config) {
            sdkKey = config.sdkKey;
          } else {
            sdkKey = config.envKey;
          }

          if (!sdkKey) {
            throw new Error('You must provide a sdkKey to asyncWithDVCProvider');
          }

          client = initializeDevCycleClient(sdkKey, user, options);
          return [4
          /*yield*/
          , client.onClientInitialized()];

        case 1:
          _a.sent();

          return [2
          /*return*/
          , function (_a) {
            var children = _a.children;
            return jsx(Provider, __assign({
              value: {
                client: client
              }
            }, {
              children: children
            }));
          }];
      }
    });
  });
}

var useDevCycleClient = function useDevCycleClient() {
  var dvcContext = useContext(context);
  if (dvcContext === undefined) throw new Error('useDevCycleClient must be used within DevCycleProvider');
  return dvcContext.client;
};
/**
 * @deprecated use useDevCycleClient instead
 */

var useDVCClient = useDevCycleClient;

var useVariable = function useVariable(key, defaultValue) {
  var dvcContext = useContext(context);

  var _a = useState({});
      _a[0];
      var forceRerender = _a[1];

  var forceRerenderCallback = useCallback(function () {
    return forceRerender({});
  }, []);
  if (dvcContext === undefined) throw new Error('useVariable must be used within DevCycleProvider');
  useEffect(function () {
    dvcContext.client.subscribe("variableUpdated:".concat(key), forceRerenderCallback);
    return function () {
      dvcContext.client.unsubscribe("variableUpdated:".concat(key), forceRerenderCallback);
    };
  }, [dvcContext, key, forceRerenderCallback]);
  return dvcContext.client.variable(key, defaultValue);
};

var useVariableValue = function useVariableValue(key, defaultValue) {
  return useVariable(key, defaultValue).value;
};

function DevCycleProvider(props) {
  var config = props.config;
  var user = config.user,
      options = config.options;

  var _a = useState(false),
      isInitialized = _a[0],
      setIsInitialized = _a[1];

  var clientRef = useRef();
  var initializedWatcher = useRef(isInitialized);
  var sdkKey;

  if ('sdkKey' in config) {
    sdkKey = config.sdkKey;
  } else {
    sdkKey = config.envKey;
  }

  if (!sdkKey) {
    throw new Error('You must provide a sdkKey to DevCycleProvider');
  }

  if (!clientRef.current) {
    // if on server, set deferInitialization to true
    clientRef.current = initializeDevCycleClient(sdkKey, user, __assign(__assign({}, options), {
      deferInitialization: typeof window === 'undefined'
    }));
  } // ensure there is exactly one initialization event handler per provider
  // use a ref so we don't re-render while setting this up


  if (!initializedWatcher.current) {
    initializedWatcher.current = true;
    clientRef.current.onClientInitialized().then(function () {
      setIsInitialized(true);
    }).catch(function () {
      // set to true to unblock app load
      console.log('Error initializing DevCycle.');
      setIsInitialized(true);
    });
  }

  useEffect(function () {
    return function () {
      var _a;

      (_a = clientRef.current) === null || _a === void 0 ? void 0 : _a.close();
      clientRef.current = undefined;
    };
  }, []);
  return jsx(Provider, __assign({
    value: {
      client: clientRef.current
    }
  }, {
    children: jsx(initializedContext.Provider, __assign({
      value: {
        isInitialized: isInitialized
      }
    }, {
      children: props.children
    }))
  }));
}
/**
 * @deprecated Use DevCycleProvider instead
 */

var DVCProvider = DevCycleProvider;

function withDevCycleProvider(config) {
  return function withDVCProviderHOC(WrappedComponent) {
    var HoistedComponent =
    /** @class */
    function (_super) {
      __extends(HoistedComponent, _super);

      function HoistedComponent() {
        return _super !== null && _super.apply(this, arguments) || this;
      }

      HoistedComponent.prototype.render = function () {
        return jsx(DevCycleProvider, __assign({
          config: config
        }, {
          children: jsx(WrappedComponent, __assign({}, this.props))
        }));
      };

      return HoistedComponent;
    }(React.Component);

    hoistNonReactStatics(HoistedComponent, WrappedComponent);
    return HoistedComponent;
  };
}
/**
 * @deprecated Use withDevCycleProvider instead
 */

var withDVCProvider = withDevCycleProvider;

/**
 *
 * @deprecated Use the `useVariable` hook instead
 *
 */

var useDVCVariable = function useDVCVariable(key, defaultValue) {
  return useVariable(key, defaultValue);
};

var useIsDevCycleInitialized = function useIsDevCycleInitialized() {
  var context = useContext(initializedContext);
  if (context === undefined) throw new Error('useIsDevCycleInitialized must be used within DevCycleProvider');
  return context.isInitialized;
};
/**
 * @deprecated use useIsDevCycleInitialized instead
 */

var useIsDVCInitialized = useIsDevCycleInitialized;

export { DVCProvider, DevCycleProvider, asyncWithDVCProvider, useDVCClient, useDVCVariable, useDevCycleClient, useIsDVCInitialized, useIsDevCycleInitialized, useVariable, useVariableValue, withDVCProvider, withDevCycleProvider };
